import { GroupedMessageResult } from '../../types/groupedMessageResult';
import { deserializeHandshakeRequestPayload } from '../serialization/deserializeHandshakeRequestPayload';
import { deserializeHandshakeConfirmPayload } from '../serialization/deserializeHandshakeConfirmPayload';
import { deserializeMessagePayload } from '../serialization/deserializeMessagePayload'
import { handshakeRequestPayload } from '../../types/handshakeRequestPayload';
import { handshakeConfirmPayload } from '../../types/handshakeConfirmPayload';
import { messagePayload } from '../../types/messagePayload';
import { MESSAGE_TYPE_HANDSHAKE_REQUEST, MESSAGE_TYPE_HANDSHAKE_CONFIRM, MESSAGE_TYPE_TEXT } from '../../constants/message';

/**
 * Represents a parsed or unparsed grouped message depending on its completeness and type.
 */
export type ParsedMessage =
  | {
      isParsed: true;
      messageId: string;
      messageType: number;
      parsed: handshakeRequestPayload;
    }
  | {
      isParsed: false;
      messageId: string;
      messageType: number;
      raw: GroupedMessageResult;
    }
  | {
      isParsed: true;
      messageId: string;
      messageType: number;
      parsed: handshakeConfirmPayload;
  }
  | {
      isParsed: true;
      messageId: string;
      messageType: number;
      parsed: messagePayload;
  }
  | {
      isParsed: true;
      messageId: string;
      messageType: number;
      parsed: Uint8Array;
  };

/**
 * Parses a grouped message into a higher-level structured object if it's complete and recognized.
 * Falls back to a raw representation if the message is incomplete or of unknown type.
 *
 * @param grouped - A reassembled message, potentially incomplete
 * @returns A parsed message object (or a wrapper around the raw one)
 */
export function parseKnownPayloadFromGroup(grouped: GroupedMessageResult): ParsedMessage {
  const { messageId, messageType, isComplete, assembledPayload } = grouped;

  if (!isComplete || !assembledPayload) {
    return {
      isParsed: false,
      messageId,
      messageType,
      raw: grouped,
    };
  }

  switch (messageType) {
    case MESSAGE_TYPE_HANDSHAKE_REQUEST:
      return {
        isParsed: true,
        messageId,
        messageType,
        parsed: deserializeHandshakeRequestPayload(assembledPayload),
      };

    case MESSAGE_TYPE_HANDSHAKE_CONFIRM:
      return {
        isParsed: true,
        messageId,
        messageType,
        parsed: deserializeHandshakeConfirmPayload(assembledPayload),
      };  

    case MESSAGE_TYPE_TEXT:
      return {
        isParsed: true,
        messageId,
        messageType,
        parsed: deserializeMessagePayload(assembledPayload),
      };

    default:

      return {
        isParsed: true,
        messageId,
        messageType,
        parsed: assembledPayload,
      };
  }
}